import * as lodash from "lodash";
import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import { Observable } from "rxjs";

import {
    ITCIApplication,
    ITCIAssetTag,
    ITCIAssetTagsInfo,
    ITCIAssetTagsList,
    ITCIConnection,
    ITCIMarketplaceListing,
    ITCISimplifiedAssetTagsList,
    SERVICE_URLS
} from "@tci/common";

@Injectable()
export class NgTagsService {
    static instance: NgTagsService;

    static getInstance(http: Http): NgTagsService {
        if (!NgTagsService.instance) {
            NgTagsService.instance = new NgTagsService(http);
        }
        return NgTagsService.instance;
    }

    private constructor(private http: Http) {

    }

    /**
     * Count how many applications to which every tag is attached.
     *
     * @author Kevin Li <huali@tibco-support.com>
     * @param tagsList {ITCIAssetTagsList} The tags list retrieved from the back-end API.
     * @param existingAssets {()ITCIApplication | ITCIConnection)[]} If not null or undefined, the existing assets will
     * be used to do intersection with the asset ids to make sure only existing assets are counted.
     * @returns {[tagId: string]: number} An object in which the keys are the tag ids and the values are the number of
     * assets to which the tag is attached.
     */
    countAppsByTags(
        tagsList: ITCIAssetTagsList,
        existingAssets?: (ITCIApplication | ITCIConnection | ITCIMarketplaceListing)[]
    ): { [tagId: string]: number } {
        const simplifiedTagsList: ITCISimplifiedAssetTagsList = this.simplifyTagsList(tagsList);
        return lodash
            .chain(simplifiedTagsList)
            .mapValues((assetIds: string[]) => {
                return lodash.isArray(existingAssets) ?
                    lodash.intersection(
                        assetIds,
                        lodash.map(existingAssets, (asset: ITCIApplication | ITCIConnection | ITCIMarketplaceListing) => asset.id)
                    ) :
                    assetIds;
            })
            .mapValues((assetIds: string[]) => assetIds.length)
            .value();
    }

    /**
     * Simplify the data structure of the values in the tags list.
     * This will convert the data structure from
     * {
     *     tagId: [
     *         {
     *             tenantType: [
     *                 ...appId,
     *                 ...connectionId
     *             ]
     *         }
     *     ]
     * }
     * to
     * {
     *     tagId: [
     *         ...appId,
     *         ...connectionId
     *     ]
     * }
     *
     * @author Kevin Li <huali@tibco-support.com>
     * @param tagsList {ITCIAssetTagsList} The tags list retrieved from the back-end API.
     * @returns {ITCISimplifiedAssetTagsList} A simplified version of tags list by keeping only the tag ids and the
     * asset ids.
     */
    simplifyTagsList(tagsList: ITCIAssetTagsList): ITCISimplifiedAssetTagsList {
        return lodash
            .chain(tagsList)
            .get("tags")
            .keyBy("name")
            .mapValues((tagsInfo: ITCIAssetTagsInfo) => lodash.union(
                tagsInfo.agentIds,
                tagsInfo.appIds,
                tagsInfo.connectionIds,
                tagsInfo.marketplaceIds
            ))
            .value();
    }

    /**
     * Get the tags list from the back-end API.
     *
     * @author Kevin Li <huali@tibco-support.com>
     * @returns Observable<ITCIAssetTagsList>
     */
    getAllTags(): Observable<ITCIAssetTagsList> {
        return this
            .http
            .get(`${SERVICE_URLS.TAGS}`)
            .map((res: Response) => {
                return res && (res.json() as ITCIAssetTagsList) || null;
            })
            .catch(error => {
                console.log("Error while getting all tags: ", error);
                return Observable.of(null);
            });
    }

    /**
     * Find all the tags that are attached to every app and assign them to the `tags` property of the app.
     * This method mutates the assets by assigning the `tags` property as well as returning them as the return value.
     *
     * @author Kevin Li <huali@tibco-support.com>
     * @param assets {(ITCIApplication | ITCIConnection | ITCIMarketplaceListing)[]}
     * An array of assets to which the tags array will be assigned.
     * @param tagsList {ITCIAssetTagsList} The tags list retrieved from the back-end API.
     * @returns {(ITCIApplication | ITCIConnection | ITCIMarketplaceListing)[]} An array of assets of which the tags array is assigned.
     */
    populateTagsToAssets(
        assets: (ITCIApplication | ITCIConnection | ITCIMarketplaceListing)[],
        tagsList: ITCIAssetTagsList
    ): (ITCIApplication | ITCIConnection | ITCIMarketplaceListing)[] {
        return lodash.map(assets, (asset: ITCIApplication | ITCIConnection | ITCIMarketplaceListing) => {
            const currentAssetId: string = asset.id;
            const simplifiedTagsList: ITCISimplifiedAssetTagsList = this.simplifyTagsList(tagsList);

            asset.tags = lodash
                .chain(simplifiedTagsList)
                .pickBy((assetIds: string[]) => lodash.includes(assetIds, currentAssetId))
                .keys()
                .map((tagId: string) => ({ name: tagId }))
                .value();
            asset.tagsAdded = [];
            asset.tagsRemoved = [];

            return asset;
        });
    }

    /**
     * Concatenate the tag names into a comma separated tag names string.
     *
     * @param {ITCIAssetTag} tags
     * @returns {string} A comma separated tag names string or an empty string if there are no tags.
     */
    concatTagNames(tags: ITCIAssetTag[]) {
        return lodash
            .map(tags, tag => tag.name)
            .join(", ");
    }

    countTagsWithApps(elements: ITCIAssetTag[], appCountPerTag: { [tagId: string]: number; }) {
        const res: ITCIAssetTag[] = [];
        for (const k of Object.keys(elements)) {
            const count = Object.assign(elements[k], { assetCount: appCountPerTag[elements[k].name] });
            res.push(count);
        }
        return res;
    }
}

